/**
 * Provides an administration interface for managing Quick Access Applications
 * available to users across different roles. Allows administrators to configure
 * role-based application visibility, maintain application URLs, and control
 * whether specific quick access links are displayed to users. Supports loading
 * existing configurations, tracking unsaved changes, and persisting updates
 * to quick link settings for each role.
 */
import React, { useEffect, useState } from "react";

import { ROLE_NAME_MAP } from "@/shared/utils/maps";
import { getQuickLinksConfig, updateQuickLinksConfig } from "@/services/widgets";
import { ChevronDown } from "@untitledui/icons";

type Props = {
  widget: string;
  onBack: () => void;
};

const QuickAppsConfig: React.FC<Props> = () => {
  const [data, setData] = useState<Record<string, any[]>>({});
  const [roles, setRoles] = useState<string[]>([]);

  const [role, setRole] = useState("");
  const [app, setApp] = useState("");
  const [url, setUrl] = useState("");
  const [isActive, setIsActive] = useState(true);

  const [initial, setInitial] = useState({ url: "", isActive: true });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const res = await getQuickLinksConfig();
    setRoles(res.roles || []);
    setData(res.data || {});
  };

  useEffect(() => {
    if (role && app && data[role]) {
      const selected = data[role].find((a) => a.name === app);
      const val = selected?.url || "";
      const active = selected?.is_active ?? false;

      setUrl(val);
      setIsActive(active);
      setInitial({ url: val, isActive: active });
    } else {
      setUrl("");
      setIsActive(true);
      setInitial({ url: "", isActive: true });
    }
  }, [role, app, data]);

  const hasChanges = url !== initial.url || isActive !== initial.isActive;

  const handleSave = async () => {
    try {
      setSaving(true);

      await updateQuickLinksConfig({
        roleCode: role,
        appName: app,
        appUrl: url,
        isActive,
      });

      setInitial({ url, isActive });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setUrl(initial.url);
    setIsActive(initial.isActive);
  };

  const apps = role ? data[role] || [] : [];

  return (
    <div className="border border-gray-200 rounded-md bg-white p-4">
      <div className="mb-4">
        <div className="text-lg font-semibold text-gray-700">
          Quick Apps Configuration
        </div>
        <div className="text-sm text-gray-500 mt-1">
          Configure application visibility and URLs by role.
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 items-end">
       <div className="flex flex-col gap-1">
  <label className="text-xs font-medium text-gray-600">Role</label>

  <div className="relative">
    <select
      className="h-9 w-full appearance-none rounded-md border border-gray-300 px-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-[var(--color-brand)]"
      value={role}
      onChange={(e) => {
        setRole(e.target.value);
        setApp("");
      }}
    >
      <option value="">Select Role</option>
      {roles.map((r) => (
        <option key={r} value={r}>
          {ROLE_NAME_MAP[r] || r}
        </option>
      ))}
    </select>

    <ChevronDown
      className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500"
    />
  </div>
</div>

     <div className="flex flex-col gap-1">
  <label className="text-xs font-medium text-gray-600">
    Application
  </label>

  <div className="relative">
    <select
      className="h-9 w-full appearance-none rounded-md border border-gray-300 px-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-[var(--color-brand)]"
      value={app}
      onChange={(e) => setApp(e.target.value)}
      disabled={!role}
    >
      <option value="">Select App</option>
      {apps.map((a) => (
        <option key={a.name} value={a.name}>
          {a.name}
        </option>
      ))}
    </select>

    <ChevronDown
      className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500"
    />
  </div>
</div>

        {app && (
          <div className="flex items-center justify-end gap-3">
            <span className="text-xs font-medium text-gray-600">
              App Visibility
            </span>

            <div className="flex overflow-hidden rounded-md border border-gray-300">
              <button
                type="button"
                className={`h-7 px-4 text-sm font-medium ${
                  isActive
                    ? "bg-[var(--color-brand)] text-white"
                    : "bg-white text-gray-600"
                }`}
                onClick={() => setIsActive(true)}
              >
                Show
              </button>

              <button
                type="button"
                className={`h-7 px-4 text-sm font-medium ${
                  !isActive
                    ? "bg-[var(--color-brand)] text-white"
                    : "bg-white text-gray-600"
                }`}
                onClick={() => setIsActive(false)}
              >
                Hide
              </button>
            </div>
          </div>
        )}
      </div>

      {app && isActive && (
        <div className="mt-4 flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-600">
            Configure URL
          </label>
          <input
            type="text"
            placeholder="Enter URL"
            className="h-9 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-[var(--color-brand)]"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
        </div>
      )}

      <div className="mt-4 flex items-center justify-end gap-2">
        {hasChanges && (
          <div className="rounded border border-orange-300 bg-orange-50 px-2 py-0.5 text-xs text-orange-600">
            Unsaved Changes
          </div>
        )}

        <button
          className="rounded bg-[#e7e5e0] px-3 py-1 text-xs"
          onClick={handleCancel}
          disabled={!hasChanges || saving}
        >
          Cancel
        </button>

        <button
          className="rounded bg-[var(--color-brand)] px-3 py-1 text-xs text-white"
          onClick={handleSave}
          disabled={!role || !app || !hasChanges || saving}
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
};

export default QuickAppsConfig;